/* Progressive commerce: native Shopify forms remain usable without this file.
 * Hooks: form[data-product-form] (id, quantity), [data-cart-open], [data-cart-count].
 * Cart writes are serialized and render Shopify's returned sections; no client price math.
 */
(() => {
  'use strict';
  const rootPath = window.Shopify?.routes?.root || '/';
  const route = (path) => `${rootPath}${path}`;
  let mutating = false;
  let revision = 0;
  let drawerOpener;
  let imageOpener;
  const english = document.documentElement.lang === 'en';
  const say = (tr, en) => (english ? en : tr);
  const variantCache = new WeakMap();
  const drawer = () => document.querySelector('[data-cart-dialog]');
  const sectionIds = () => ['hc-cart-drawer', document.querySelector('[data-cart-page]')?.dataset.cartSectionId].filter(Boolean);

  async function request(url, options = {}) {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 20000);
    try {
      const response = await fetch(url, { ...options, signal: controller.signal, headers: { Accept: 'application/json', ...options.headers } });
      const data = await response.json();
      if (!response.ok) throw new Error(typeof data.description === 'string' ? data.description : typeof data.message === 'string' ? data.message : say('Sepet güncellenemedi. Lütfen tekrar deneyin.', 'The cart could not be updated. Please try again.'));
      return data;
    } finally { window.clearTimeout(timeout); }
  }

  function showError(scope, message, selector = '[data-product-error]') {
    let node = scope?.querySelector(selector);
    if (!node && scope) {
      node = document.createElement('p');
      node.className = 'commerce-error';
      node.setAttribute('role', 'alert');
      node.setAttribute(selector.slice(1, -1), '');
      scope.append(node);
    }
    if (node) { node.textContent = message; node.hidden = !message; }
  }

  function setCartBusy(busy) {
    document.querySelectorAll('[data-cart-content]').forEach((content) => {
      content.setAttribute('aria-busy', String(busy));
      content.querySelectorAll('[data-cart-quantity], [data-quantity-step], [data-cart-checkout], [data-quick-add]').forEach((control) => { control.disabled = busy; if (control.matches('[data-quick-add]')) control.setAttribute('aria-disabled', String(busy)); });
    });
  }

  function syncCount(count) {
    if (!Number.isFinite(count)) return;
    document.querySelectorAll('[data-cart-count]').forEach((node) => { node.textContent = String(count); node.dataset.count = String(count); });
    document.dispatchEvent(new CustomEvent('herbaclean:cart-updated', { detail: { itemCount: count } }));
  }

  function formatMoney(scope = document) {
    scope.querySelectorAll('[data-money-cents]').forEach((node) => {
      const cents = Number(node.dataset.moneyCents);
      if (!Number.isFinite(cents)) return;
      const currency = node.dataset.currency || window.Shopify?.currency?.active || 'TRY';
      const isTurkishCart = document.documentElement.lang.startsWith('tr') && currency === 'TRY' && node.closest('[data-cart-content]');
      node.textContent = isTurkishCart
        ? new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(cents / 100) + ' TL'
        : new Intl.NumberFormat(document.documentElement.lang || 'tr-TR', { style: 'currency', currency }).format(cents / 100);
    });
  }

  function renderSections(sections, count) {
    if (!sections) return false;
    let drawerRendered = false;
    for (const html of Object.values(sections)) {
      if (typeof html !== 'string') continue;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      for (const selector of ['[data-cart-dialog]', '[data-cart-page]']) {
        const next = parsed.querySelector(selector);
        const current = document.querySelector(selector);
        if (next && current) {
          current.innerHTML = next.innerHTML;
          if (selector === '[data-cart-dialog]') drawerRendered = true;
          const serverCount = Number(next.querySelector('[data-item-count]')?.dataset.itemCount);
          if (Number.isFinite(serverCount)) count = serverCount;
        }
      }
    }
    formatMoney();
    syncCount(count);
    return drawerRendered;
  }

  async function refreshCart() {
    const requestRevision = revision;
    const url = new URL(window.location.href);
    url.searchParams.set('sections', sectionIds().join(','));
    url.searchParams.set('_cart', String(Date.now()));
    const sections = await request(url.toString(), { cache: 'no-store', headers: { Accept: '*/*' } });
    if (requestRevision !== revision) return;
    if (!renderSections(sections)) {
      const cart = await request(route('cart.js'), { cache: 'no-store' });
      if (requestRevision === revision) syncCount(cart.item_count);
      throw new Error(say('Sepet görünümü yüklenemedi. Sepetin tamamını açarak devam edebilirsiniz.', 'The cart view could not be loaded. Open the full cart to continue.'));
    }
  }

  function openDrawer(opener) {
    const dialog = drawer();
    if (!dialog || typeof dialog.showModal !== 'function') { window.location.assign(route('cart')); return; }
    if (opener) drawerOpener = opener;
    if (!dialog.open) dialog.showModal();
    document.body.classList.add('commerce-drawer-open');
  }

  function closeDrawer() {
    drawer()?.close();
    document.body.classList.remove('commerce-drawer-open');
    if (drawerOpener?.isConnected) drawerOpener.focus({ preventScroll: true });
  }

  function recoverMessage(error) {
    return error.name === 'AbortError' || error instanceof TypeError || error instanceof SyntaxError
      ? say('Sepet yanıtı alınamadı. Tekrar eklemeden önce sepetinizi kontrol edin.', 'The cart did not respond. Check your cart before adding the item again.')
      : error.message;
  }

  async function addProduct(form, quickButton) {
    const scope = form || quickButton.closest('[data-complementary]');
    showError(scope, '');
    if (mutating) return;
    if (form && !form.reportValidity()) return;
    mutating = true;
    revision += 1;
    const button = quickButton || form.querySelector('[data-add-button]') || form.querySelector('[type="submit"]');
    const label = button?.querySelector('[data-add-label]');
    const originalLabel = label?.textContent;
    if (button) { button.disabled = true; button.setAttribute('aria-busy', 'true'); button.setAttribute('aria-disabled', 'true'); }
    if (label) label.textContent = 'Ekleniyor…';
    setCartBusy(true);
    try {
      let body = form ? new FormData(form) : new FormData();
      let headers;
      const addon = form?.querySelector('[data-product-addon]:checked:not(:disabled)');
      if (addon) {
        // One optional pack contains two bottles, irrespective of the soap quantity.
        const soap = { id: Number(body.get('id')), quantity: Number(body.get('quantity') || 1) };
        const sellingPlan = body.get('selling_plan');
        if (sellingPlan) soap.selling_plan = sellingPlan;
        for (const [key, value] of body.entries()) {
          const property = key.match(/^properties\[(.+)\]$/);
          if (property && typeof value === 'string') (soap.properties ||= {})[property[1]] = value;
        }
        body = JSON.stringify({
          items: [soap, { id: Number(addon.value), quantity: 1 }],
          sections: sectionIds(), sections_url: window.location.pathname,
        });
        headers = { 'Content-Type': 'application/json' };
      } else {
        if (quickButton) { body.set('id', quickButton.dataset.quickAdd); body.set('quantity', '1'); }
        body.set('sections', sectionIds().join(','));
        body.set('sections_url', window.location.pathname);
      }
      const result = await request(route('cart/add.js'), { method: 'POST', body, headers });
      if (!renderSections(result.sections, result.item_count)) {
        try { await refreshCart(); } catch { window.location.assign(route('cart')); return; }
      }
      openDrawer(drawer()?.open ? null : button?.isConnected ? button : document.querySelector('[data-cart-open]'));
      drawer()?.querySelector('[data-cart-close]')?.focus({ preventScroll: true });
    } catch (error) {
      // A lost response is ambiguous. Only read back; never retry a cart write.
      try { await refreshCart(); } catch { /* Keep the original actionable error. */ }
      const cartScope = !form && quickButton.closest('[data-cart-content]')
        ? (drawer()?.open ? drawer().querySelector('[data-cart-content]') : document.querySelector('[data-cart-page] [data-cart-content]')) : null;
      showError(form || cartScope || scope, recoverMessage(error), cartScope ? '[data-cart-error]' : '[data-product-error]');
    } finally {
      mutating = false;
      setCartBusy(false);
      if (button) { button.disabled = false; button.removeAttribute('aria-busy'); button.removeAttribute('aria-disabled'); }
      if (label) label.textContent = originalLabel;
      const productRoot = form?.closest('[data-product-root]');
      if (productRoot) updateVariant(productRoot, false);
    }
  }

  async function changeQuantity(line, quantity, focusId) {
    if (mutating || !Number.isInteger(quantity) || quantity < 0) return;
    const key = line.dataset.cartKey;
    if (!key) return;
    mutating = true;
    revision += 1;
    const content = line.closest('[data-cart-content]');
    showError(content, '', '[data-cart-error]');
    setCartBusy(true);
    try {
      const result = await request(route('cart/change.js'), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity, sections: sectionIds(), sections_url: window.location.pathname }),
      });
      if (!renderSections(result.sections, result.item_count)) {
        try { await refreshCart(); } catch { window.location.assign(route('cart')); return; }
      }
      if (focusId) document.getElementById(focusId)?.focus({ preventScroll: true });
      if (drawer()?.open && !drawer().contains(document.activeElement)) drawer().querySelector('[data-cart-close]')?.focus({ preventScroll: true });
    } catch (error) {
      try { await refreshCart(); } catch { /* Render the error in the existing cart if readback also fails. */ }
      const currentContent = drawer()?.open ? drawer().querySelector('[data-cart-content]') : document.querySelector('[data-cart-page] [data-cart-content]');
      showError(currentContent || content, recoverMessage(error), '[data-cart-error]');
    } finally { mutating = false; setCartBusy(false); }
  }

  function productData(productRoot) {
    if (!variantCache.has(productRoot)) {
      try { variantCache.set(productRoot, JSON.parse(productRoot.querySelector('[data-product-variants]').textContent)); }
      catch { variantCache.set(productRoot, null); }
    }
    return variantCache.get(productRoot);
  }

  function selectMedia(productRoot, id) {
    if (!id || !productRoot.querySelector(`[data-product-media="${id}"]`)) return;
    productRoot.querySelectorAll('[data-product-media]').forEach((frame) => {
      frame.hidden = frame.dataset.productMedia !== String(id);
      if (frame.hidden) frame.querySelectorAll('video').forEach((video) => video.pause());
    });
    productRoot.querySelectorAll('[data-media-target]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.mediaTarget === String(id))));
  }

  function openImage(link) {
    const dialog = link.closest('[data-product-root]')?.querySelector('[data-product-viewer]');
    if (!dialog?.showModal) return false;
    document.querySelectorAll('dialog[open]').forEach((other) => other.close());
    imageOpener = link;
    const status = dialog.querySelector('[data-image-status]');
    status.textContent = say('Görsel yükleniyor…', 'Loading the image…');
    dialog.setAttribute('aria-busy', 'true');
    const img = new Image();
    img.alt = link.querySelector('img')?.alt || '';
    img.draggable = false;
    img.onload = () => { if (!img.isConnected) return; dialog.removeAttribute('aria-busy'); status.textContent = ''; };
    img.onerror = () => { if (!img.isConnected) return; dialog.removeAttribute('aria-busy'); status.textContent = say('Görsel yüklenemedi. Kapatıp tekrar deneyin.', 'The image could not be loaded. Close it and try again.'); };
    dialog.querySelector('[data-image-canvas]').replaceChildren(img);
    img.src = link.href;
    dialog.showModal();
    return true;
  }

  function updateVariant(productRoot, updateUrl) {
    const data = productData(productRoot);
    const form = productRoot.querySelector('[data-product-form]');
    if (!data || !form) return;
    const id = new FormData(form).get('id');
    const variant = data.variants.find((entry) => String(entry.id) === id);
    if (!variant) return;
    const money = new Intl.NumberFormat(document.documentElement.lang || 'tr-TR', { style: 'currency', currency: data.currency || 'TRY' });
    const price = productRoot.querySelector('[data-product-price]');
    if (price) price.textContent = money.format(variant.price / 100);
    const discounted = variant.compareAtPrice > variant.price;
    productRoot.querySelectorAll('[data-product-compare], [data-sticky-compare]').forEach((compare) => {
      compare.hidden = !discounted;
      compare.textContent = money.format((variant.compareAtPrice || 0) / 100);
    });
    productRoot.querySelectorAll('[data-product-discount]').forEach((badge) => { badge.hidden = !discounted; });
    const title = productRoot.querySelector('[data-selected-variant]');
    if (title) title.textContent = variant.title;
    const button = form.querySelector('[data-add-button]');
    if (button) { button.disabled = !variant.available || mutating; button.querySelector('[data-add-label]').textContent = variant.available ? say('Sepete ekle', 'Add to cart') : say('Tükendi', 'Sold out'); }
    const availability = productRoot.querySelector('[data-product-availability]');
    if (availability) availability.textContent = variant.available ? say('Stokta', 'In stock') : say('Bu seçenek şu anda stokta yok.', 'This option is out of stock right now.');
    const stock = productRoot.querySelector('[data-low-stock]');
    if (stock) { stock.hidden = !(variant.available && variant.stock > 0 && variant.stock <= 5); stock.textContent = `Stokta ${variant.stock} adet kaldı.`; }
    const stickyPrice = productRoot.querySelector('[data-sticky-price]');
    if (stickyPrice) stickyPrice.textContent = money.format(variant.price / 100);
    const stickyButton = productRoot.querySelector('[data-sticky-add]');
    if (stickyButton) { stickyButton.disabled = !variant.available || mutating; stickyButton.textContent = variant.available ? say('Sepete ekle', 'Add to cart') : say('Tükendi', 'Sold out'); }
    productRoot.dispatchEvent(new CustomEvent('herbaclean:variant-updated', { bubbles: true }));
    const quantity = form.querySelector('[data-product-quantity]');
    if (quantity) {
      const min = variant.min || 1;
      const step = variant.step || 1;
      quantity.min = String(min);
      quantity.step = String(step);
      if (variant.max > 0) quantity.max = String(variant.max); else quantity.removeAttribute('max');
      const previous = Number(quantity.value) || min;
      const next = Math.max(min, min + Math.floor((previous - min) / step) * step);
      const maximum = variant.max >= min ? min + Math.floor((variant.max - min) / step) * step : null;
      quantity.value = String(maximum === null ? next : Math.min(maximum, next));
    }
    if (updateUrl) {
      selectMedia(productRoot, variant.mediaId);
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
      showError(form, '');
    }
  }

  // Own product submits before Shopify's form listener so one AJAX add emits one analytics event.
  document.addEventListener('submit', (event) => {
    if (event.target.matches('form[data-product-form]')) { event.preventDefault(); void addProduct(event.target); }
    if (event.target.matches('.cart-form') && mutating) event.preventDefault();
  }, true);

  document.addEventListener('click', (event) => {
    const quickButton = event.target.closest('[data-quick-add]');
    if (quickButton && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) { event.preventDefault(); void addProduct(null, quickButton); return; }
    const imageLink = event.target.closest('[data-image-open]');
    if (imageLink && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
      if (openImage(imageLink)) event.preventDefault();
      return;
    }
    const viewer = event.target.closest('[data-product-viewer]');
    if (viewer) { viewer.close(); return; }
    const opener = event.target.closest('[data-cart-open]');
    if (opener && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
      event.preventDefault(); openDrawer(opener);
      if (!mutating) {
        const dialog = drawer();
        dialog?.setAttribute('aria-busy', 'true');
        void refreshCart().catch((error) => showError(dialog?.querySelector('[data-cart-content]'), error.message, '[data-cart-error]')).finally(() => {
          dialog?.removeAttribute('aria-busy');
          if (dialog?.open && !dialog.contains(document.activeElement)) dialog.querySelector('[data-cart-close]')?.focus({ preventScroll: true });
        });
      }
      return;
    }
    if (event.target.closest('[data-cart-close]')) { closeDrawer(); return; }
    if (event.target === drawer()) {
      const bounds = drawer().getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) closeDrawer();
      return;
    }
    const thumbnail = event.target.closest('[data-media-target]');
    if (thumbnail) { selectMedia(thumbnail.closest('[data-product-root]'), thumbnail.dataset.mediaTarget); return; }
    const stepButton = event.target.closest('[data-quantity-step]');
    if (stepButton) {
      const input = stepButton.closest('[data-quantity-control]')?.querySelector('input');
      if (!input || input.disabled) return;
      const oldValue = input.value;
      if (Number(stepButton.dataset.quantityStep) > 0) input.stepUp(); else input.stepDown();
      if (input.value !== oldValue) input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }
    const remove = event.target.closest('[data-cart-remove]');
    if (remove) { event.preventDefault(); void changeQuantity(remove.closest('[data-cart-line]'), 0); }
  });

  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-variant-choice]')) updateVariant(event.target.closest('[data-product-root]'), true);
    if (event.target.matches('[data-filter-choice], [data-collection-sort]')) event.target.form?.requestSubmit();
    if (event.target.matches('[data-cart-quantity]')) {
      if (!event.target.reportValidity()) return;
      void changeQuantity(event.target.closest('[data-cart-line]'), Number(event.target.value), event.target.id);
    }
  });

  document.addEventListener('close', (event) => {
    if (!event.target.matches('[data-product-viewer]')) return;
    if (imageOpener?.isConnected) imageOpener.focus({ preventScroll: true });
  }, true);

  // Native dialog supplies focus containment and Escape. Keep body scroll in sync.
  document.addEventListener('close', (event) => {
    if (event.target.matches('[data-cart-dialog]')) { document.body.classList.remove('commerce-drawer-open'); if (drawerOpener?.isConnected) drawerOpener.focus({ preventScroll: true }); }
  }, true);
  function enableProductAddons() {
    document.querySelectorAll('[data-product-addon][data-addon-available="true"]').forEach((input) => { input.disabled = false; });
  }
  document.addEventListener('shopify:section:load', () => { enableProductAddons(); formatMoney(); document.querySelectorAll('[data-product-root]').forEach((element) => updateVariant(element, false)); });
  enableProductAddons();
  formatMoney();
  document.querySelectorAll('[data-product-root]').forEach((element) => updateVariant(element, false));
})();
